import { all, call, put, select, takeLatest } from "redux-saga/effects";
import { apiDeleteCall, apiGetCall, apiPostCall, apiPutCall } from "../api";
import axios from "axios";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import {
  fetchUsers,
  fetchUsersSuccess,
  fetchUsersFailure,
  setUsers,
  setRoleSuccess,
  setRoleFailure,
  setRoleStart,
  limitStart,
  limitSuccess,
  addUserRequest,
  addUserSuccess,
  addUserFailure,
  getAdminUserDetails,
  setAdminUserDetails,
  setLanguages,
  getNotificationTypes,
  setNotifsAndPrefs,
  setAdminUserData,
  resetLoading,
  removeAdminUser,
  reactivateUser,
  importAdminUsers,
} from "../slices/userSlice";
import {
  resetAppLoading,
  setAppLoading,
  setNotification,
} from "../slices/notificationSlice";
import pako from "pako";

function s2ab(s) {
  var buf = new ArrayBuffer(s.length);
  var view = new Uint8Array(buf);
  for (var i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;
  return buf;
}

const URL = process.env.REACT_APP_API_URL;
function* checkTokenExpired() {
  yield window.instance
    .acquireTokenSilent({
      redirectUri: process.env.REACT_APP_AZURE_REDIRECT,
      authority: process.env.REACT_APP_AZURE_AUTHORITY,
    })
    .then((res) => {
      axios.defaults.headers.common["Authorization"] = `Bearer ${res.idToken}`;
    })
    .catch((e) => {
      if (e instanceof InteractionRequiredAuthError) {
        window.instance.loginRedirect();
      }
    });
}

function* fetchUsersSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    const state = yield select(({ userManagement: { currentRole } }) => ({
      currentRole,
    }));

    yield put(
      setUsers({
        isInput: action.payload.isInput,
        loading: true,
        change: null,
        ...(action.payload.params?.searchTerm && {
          searchTerm: action.payload.params.searchTerm,
        }),
      })
    );
    const response = yield call(
      apiGetCall,
      `${URL}${"admin/user-management?"}${new URLSearchParams(
        action.payload.params
      ).toString()}`
    );

    yield put(
      setUsers({
        ...response,
        isInput: action.payload.isInput,
        loading: false,
        change:
          action.payload.params.filter === state.currentRole ||
          action.payload.isInput,
      })
    );

    yield put(fetchUsersSuccess(response.data));
  } catch (error) {
    yield put(fetchUsersFailure());
    yield put(
      setNotification({
        message: "Unable to fetch users.",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(setRoleFailure());

    yield put(resetAppLoading());
  }
}

function* fetchRolesSaga(action) {
  try {
    yield call(checkTokenExpired);
    const response = yield call(apiGetCall, URL + "admin/admin-user-roles");

    yield put(setRoleSuccess(response.data));
    yield put(setLanguages(response.languages));
  } catch (error) {
    yield put(setRoleFailure());
    yield put(
      setNotification({
        message: "Unable to fetch user roles.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* limitSaga(action) {
  try {
    yield call(checkTokenExpired);
    const response = yield call(apiGetCall, URL + "admin/page-limit");
    yield put(limitSuccess(response.data));
  } catch (error) {
    yield put(setRoleFailure());
    yield put(
      setNotification({
        message: "Unable to fetch page limit.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* getAdminUserDetailsSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    const response = yield call(
      apiGetCall,
      URL +
        `admin/user-management/${
          action.payload.admin_user_id || action.admin_user_id
        }`
    );
    // console.log(response)
    yield put(setAdminUserDetails(response.data));
  } catch (error) {
    yield put(setRoleFailure());
    yield put(
      setNotification({
        message: "Unable to fetch user details.",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(resetAppLoading());
  }
}

function* setAdminUserDataSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    yield call(
      apiPutCall,
      URL +
        `admin/user-management/${action.payload.admin_user_id}?type=${action.payload.type}`,
      action.payload.data
    );
    // yield put(setAdminUserDetails(response.data));
    yield put(
      setNotification({
        message: "Updated successfully.",
        severity: "success",
        open: true,
      })
    );
  } catch (error) {
    console.error(error);
    yield put(
      setNotification({
        message: "Something went wrong. Try again!",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(resetLoading());
    yield put(resetAppLoading());
  }
}

function* removeAdminUserSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    yield call(
      apiDeleteCall,
      URL + `admin/user-management/${action.payload.admin_user_id}`
    );

    if (!action.payload.fetchUserData) {
      yield getAdminUserDetailsSaga({
        payload: { admin_user_id: action.payload.admin_user_id },
      });
    } else {
      yield fetchUsersSaga({ payload: action.payload.fetchUserData });
    }

    yield put(
      setNotification({
        message:
          "The user has been deleted successfully. All the associated data has been permanently removed",
        severity: "success",
        open: true,
      })
    );
  } catch (error) {
    console.error(error);
    console.error(error.errorCode);
    if (error.errorCode === 404) {
      yield put(
        setNotification({
          message: "The user is no longer a valid Air Canada user.",
          severity: "error",
          open: true,
        })
      );
      yield fetchUsersSaga({ payload: action.payload.fetchUserData });
    } else {
      yield put(
        setNotification({
          message: "Something went wrong. Try again!",
          severity: "error",
          open: true,
        })
      );
    }
  } finally {
    yield put(resetLoading());
    yield put(resetAppLoading());
  }
}

function* getNotificationTypesSaga(action) {
  try {
    yield call(checkTokenExpired);
    const data = yield call(apiGetCall, URL + `admin/notification-types`);
    yield put(setNotifsAndPrefs([...data.preferences, ...data.notifications]));
  } catch (error) {
    console.error(error);
    yield put(
      setNotification({
        message: "Unable to fetch notification types.",
        severity: "error",
        open: true,
      })
    );
  }
}

function* addUserSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());
    const response = yield call(
      apiPostCall,
      URL + "admin/user-management",
      action.payload
    );

    if (response.status === "Successful") {
      yield put(addUserSuccess(response));
      yield put(
        setNotification({
          message: "The new user has been successfully added to the system.",
          severity: "success",
          open: true,
        })
      );
      action.payload.navigate();
    }
  } catch (error) {
    yield put(addUserFailure());
    console.log(error);
    yield put(
      setNotification({
        message: "Something went wrong. Try again!",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(resetAppLoading());
  }
}

function* reactivateUserSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    yield call(
      apiPutCall,
      URL + `admin/user-management-reactivation/${action.payload.admin_user_id}`
    );

    yield getAdminUserDetailsSaga({
      payload: { admin_user_id: action.payload.admin_user_id },
    });
    // yield fetchUsersSaga({ payload: action.payload.fetchUserData });

    yield put(
      setNotification({
        message: "User has been successfully reactivated.",
        severity: "success",
        open: true,
      })
    );
  } catch (error) {
    console.error(error.errorCode);
    if (error.errorCode === 404) {
      yield put(
        setNotification({
          message: "The user is no longer a valid Air Canada user.",
          severity: "error",
          open: true,
        })
      );
    } else {
      yield put(
        setNotification({
          message: "An error occurred. Please try again later.",
          severity: "error",
          open: true,
        })
      );
    }
  } finally {
    yield put(resetLoading());
    yield put(resetAppLoading());
  }
}

function* importAdminUsersSaga(action) {
  try {
    yield call(checkTokenExpired);
    yield put(setAppLoading());

    const { importedFile, downloadLink, summary } = action.payload;

    // Create FormData object
    const formData = new FormData();

    // Use the correct field name that works
    formData.append("admin-users", importedFile);

    // Use axios directly with minimal configuration     // Get raw binary data from server
    const response = yield call(
      axios.post,
      URL + `admin/user-management-import`,
      formData,
      {
        responseType: "arraybuffer",
      }
    );

    const data = response.data;
    const headers = response.headers;

    const uint8Array = new Uint8Array(data);

    const isGzip = uint8Array[0] === 0x1f && uint8Array[1] === 0x8b;

    let finalData;

    if (isGzip) {
      const decompressed = pako.inflate(uint8Array);

      const base64String = new TextDecoder().decode(decompressed);
      finalData = s2ab(atob(base64String));
    } else {
      const base64String = new TextDecoder().decode(uint8Array);
      finalData = s2ab(atob(base64String));
    }

    const blob = new Blob([finalData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);

    downloadLink(url);

    summary({
      successCount: headers.get("successful"),
      errorCount: headers.get("errors"),
    });
    
    if (headers.get("successful") > 0) {
      yield put(
        setNotification({
          message: "Users imported successfully.",
          severity: "success",
          open: true,
        })
      );
    }
  } catch (error) {
    console.error("Import error:", error);

    if (error.response && error.response.data instanceof ArrayBuffer) {
      const decoder = new TextDecoder("utf-8");
      const errorText = decoder.decode(error.response.data);
      console.log(errorText);

      // Try to parse as JSON
      const errorJson = JSON.parse(errorText);
      const errorCode = errorJson.Error;

      if (errorCode?.toString() === "417" || errorCode?.toString() === "418") {
        yield put(
          setNotification({
            message:
              "Incorrect excel file headers. Download sample sheet for reference.",
            severity: "error",
            open: true,
          })
        );

        return;
      } else if (errorCode?.toString() === "419") {
        yield put(
          setNotification({
            message: "Empty excel file.",
            severity: "error",
            open: true,
          })
        );

        return;
      }
    }

    yield put(
      setNotification({
        message: "Something went wrong. Try again!",
        severity: "error",
        open: true,
      })
    );
  } finally {
    yield put(resetLoading());
    yield put(resetAppLoading());
  }
}

function* sagas() {
  yield all([
    takeLatest(fetchUsers.type, fetchUsersSaga),
    takeLatest(setRoleStart.type, fetchRolesSaga),
    takeLatest(limitStart.type, limitSaga),
    takeLatest(addUserRequest.type, addUserSaga),
    takeLatest(getAdminUserDetails.type, getAdminUserDetailsSaga),
    takeLatest(getNotificationTypes.type, getNotificationTypesSaga),
    takeLatest(setAdminUserData.type, setAdminUserDataSaga),
    takeLatest(removeAdminUser.type, removeAdminUserSaga),
    takeLatest(reactivateUser.type, reactivateUserSaga),
    takeLatest(importAdminUsers.type, importAdminUsersSaga),
  ]);
}

export default sagas;
